import { ReadMoreHtml } from "@/components/read-more/ReadMoreHtml";
import {
  shouldIncludeAttachment,
  useGetAttachmentsByDocId,
  type Attachment as ApiAttachment,
} from "@/api/attachment";
import {
  useGetSculptureEditionForPreview,
  useGetSculptureEditionRelatedFields,
} from "@/api/sculpture-edition/sculpture-edition";
import { ImageFullscreen } from "@/components/modal/imageFullscreen";
import { Accordion } from "@/components/ui/accordion";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { SupportedLocale } from "@/i18n";
import { SupportTechniqueCF } from "@/lib/compute-field/shared/technique";
import {
  computeFieldCollectorNameForOverview,
  computeFieldRelatedAuctions,
  computeFieldRelatedAuthorsArtwork,
  computeFieldRelatedBibliographies,
  computeFieldRelatedExhibitions,
} from "@/lib/computeFields";
import { buildSculptureEditionProvenance } from "@/lib/sculpture-provenance";
import {
  mapSculptureInAuctionsToArtwork,
  mapSculptureInBibliographiesToArtwork,
  mapSculptureInExhibitionsToArtwork,
} from "@/lib/sculpture-relation-mappers";
import { SculptureEdition } from "@/lib/types";
import { useMemo, useState } from "react";
import { MergedDisplayData } from "../types";
import { AuctionsAccordion } from "./AuctionsAccordion";
import { BibliographyAccordion } from "./BibliographyAccordion";
import { ExhibitionsAccordion } from "./ExhibitionsAccordion";
import { HistoryAccordion } from "./HistoryAccordion";
import { RelatedWorksAccordion } from "./RelatedWorksAccordion";

interface SculptureEditionDrawerProps {
  edition: SculptureEdition;
  displayData: MergedDisplayData;
  locale: SupportedLocale;
  t: (key: string) => string;
  trigger: React.ReactNode;
}

function getLocalized(value: unknown, locale: SupportedLocale): string | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const v = record[locale];
  if (typeof v === "string" && v.trim().length > 0) return v;
  for (const k of Object.keys(record)) {
    const c = record[k];
    if (typeof c === "string" && c.trim().length > 0) return c;
  }
  return null;
}

export function SculptureEditionDrawer({
  edition,
  displayData,
  locale,
  t,
  trigger,
}: SculptureEditionDrawerProps) {
  // Gate heavy fetches on sheet open — mounting every row used to fire
  // preview + related + attachments for all editions at once.
  const [open, setOpen] = useState(false);

  const listAttachments = edition.attachments as ApiAttachment[] | undefined;
  const { data: fetchedAttachments } = useGetAttachmentsByDocId(
    open && !listAttachments?.length ? edition.id : undefined,
  );
  const attachments = listAttachments?.length
    ? listAttachments
    : fetchedAttachments;

  const previewAttachments = useMemo(() => {
    if (!attachments?.length) return [];
    return attachments.filter((a) =>
      shouldIncludeAttachment(
        a,
        (edition.sculptureEditionPublicationStatus ?? "draft") as
          | "published"
          | "draft",
      ),
    );
  }, [attachments, edition.sculptureEditionPublicationStatus]);

  const { data: relatedFieldsWithAttachments } =
    useGetSculptureEditionRelatedFields(edition.id, { enabled: open });
  // Fetch the edition with all nested relations — useGetSculptureEditions (list)
  // does not include them, so without this the drawer accordions were empty.
  const { data: fullEdition } = useGetSculptureEditionForPreview(edition.id, {
    enabled: open,
  });
  const editionWithRelations = (fullEdition ?? edition) as typeof edition &
    Record<string, any>;

  const relationNotEmpty = useMemo(() => {
    if (!relatedFieldsWithAttachments) return false;
    return (
      (relatedFieldsWithAttachments.relatedArtwork?.length ?? 0) > 0 ||
      (relatedFieldsWithAttachments.relatedAudiovisual?.length ?? 0) > 0 ||
      (relatedFieldsWithAttachments.relatedDocument?.length ?? 0) > 0 ||
      (relatedFieldsWithAttachments.relatedMultipleprint?.length ?? 0) > 0 ||
      (relatedFieldsWithAttachments.relatedPhotography?.length ?? 0) > 0 ||
      (relatedFieldsWithAttachments.relatedWriting?.length ?? 0) > 0
    );
  }, [relatedFieldsWithAttachments]);

  const author = useMemo(
    () =>
      computeFieldRelatedAuthorsArtwork(displayData, {
        language: locale,
      }),
    [displayData, locale],
  );

  const sculptureProvenanceCalculated = useMemo(() => {
    const history = editionWithRelations.history as any[] | undefined;
    const auctions = editionWithRelations.sculptureEditionInAuction as
      | any[]
      | undefined;
    return buildSculptureEditionProvenance({
      history: history as any,
      auctions: auctions as any,
      t,
    });
  }, [
    editionWithRelations.history,
    editionWithRelations.sculptureEditionInAuction,
    t,
  ]);

  const sculptureExhibitionsCalculated = useMemo(() => {
    const adapted = mapSculptureInExhibitionsToArtwork(
      editionWithRelations.sculptureEditionInExhibitions,
    );
    if (!adapted) return null;
    return computeFieldRelatedExhibitions(
      { relatedExhbitions: adapted as any },
      { language: locale },
    );
  }, [editionWithRelations.sculptureEditionInExhibitions, locale]);

  const sculptureAuctionsCalculated = useMemo(() => {
    const adapted = mapSculptureInAuctionsToArtwork(
      editionWithRelations.sculptureEditionInAuction,
    );
    if (!adapted) return null;
    return computeFieldRelatedAuctions(
      { relatedAuctions: adapted as any },
      { language: locale },
    );
  }, [editionWithRelations.sculptureEditionInAuction, locale]);

  const sculptureBibliographyCalculated = useMemo(() => {
    const adapted = mapSculptureInBibliographiesToArtwork(
      editionWithRelations.sculptureEditionInBibliography,
    );
    if (!adapted) return null;
    return computeFieldRelatedBibliographies(
      { relatedBibliographies: adapted as any },
      { language: locale },
    );
  }, [editionWithRelations.sculptureEditionInBibliography, locale]);

  const technique = edition.sculptureEditionTechnique as
    | Record<string, string>
    | undefined;
  const support = edition.sculptureEditionSupport as
    | Record<string, string>
    | undefined;
  const materiau = getLocalized(edition.sculptureEditionMateriau, locale);
  const numerotation = getLocalized(
    edition.sculptureEditionNumerotation,
    locale,
  );
  const signature = getLocalized(edition.sculptureEditionSignature, locale);
  const marqueTampons = (edition as any).sculptureEditionMarqueTamponsPublished
    ? getLocalized((edition as any).sculptureEditionMarqueTampons, locale)
    : null;

  const hasTechniqueSupport =
    (technique && getLocalized(technique, locale)) ||
    (support && getLocalized(support, locale));

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto pb-8"
      >
        <SheetHeader>
          <SheetTitle className="text-center">
            {edition.sculptureEditionNumeroEnregistrement}
          </SheetTitle>
        </SheetHeader>
        <div className="flex flex-col items-center gap-4 mt-6">
          {previewAttachments.length > 0 && (
            <div className="w-full max-w-md flex justify-center">
              <ImageFullscreen
                media={{
                  attachments: previewAttachments,
                  type: "sculptureEdition",
                  alt:
                    edition.sculptureEditionNumeroEnregistrement ||
                    "Sculpture edition",
                  width: 600,
                  height: 600,
                  className: "object-contain max-h-[400px] w-auto",
                }}
              />
            </div>
          )}

          {author && (
            <p className="font-bold text-[15px] text-center w-full">
              {t("tirageDescriptionAuteurLabelFront") + " "}
              <span dangerouslySetInnerHTML={{ __html: author }} />
            </p>
          )}

          {edition.sculptureEditionNumeroEnregistrement && (
            <p className="font-normal text-sm text-center w-full">
              {t("tirageDescriptionNumeroEnregistrementLabelFront") + " "}
              {edition.sculptureEditionNumeroEnregistrement}
            </p>
          )}

          {edition.sculptureEditionNumeroInventaire && (
            <p className="font-normal text-sm text-center w-full">
              {t("tirageDescriptionNumeroInventaireLabelFront") + " "}
              {edition.sculptureEditionNumeroInventaire}
            </p>
          )}

          {edition.sculptureEditionTypeEdition && (
            <p className="font-normal text-sm text-center w-full">
              {t("singleArtworkSubtitleSculptureEditionType") + ": "}
              {edition.sculptureEditionTypeEdition}
            </p>
          )}

          {numerotation && (
            <p className="font-normal text-sm text-center w-full">
              {t("singleArtworkSubtitleSculptureEditionNumber") + ": "}
              {numerotation}
            </p>
          )}

          {edition.sculptureEditionDateRealisation && (
            <p className="font-normal text-sm text-center w-full">
              {t("singleArtworkSubtitleSculptureEditionDate") + ": "}
              {edition.sculptureEditionDateRealisation}
            </p>
          )}

          {edition.sculptureEditionFondeurFabricant && (
            <p className="font-normal text-sm text-center w-full">
              {t("singleArtworkSubtitleSculptureEditionFondeur") + ": "}
              {edition.sculptureEditionFondeurFabricant}
            </p>
          )}

          {edition.sculptureEditionNumeroFonte && (
            <p className="font-normal text-sm text-center w-full">
              {t("singleArtworkSubtitleSculptureEditionNumeroFonte") + ": "}
              {edition.sculptureEditionNumeroFonte}
            </p>
          )}

          {hasTechniqueSupport && (
            <p className="font-normal text-sm text-center w-full">
              {t("singleArtworkSubtitleSculptureEditionTechniqueSupport") +
                ": "}
              <SupportTechniqueCF
                input={{
                  technique: (technique || {}) as Record<string, string>,
                  support: (support || {}) as Record<string, string>,
                  preposition: t("computeField.techniqueSupportPreposition"),
                }}
                language={locale}
                lineBreak={false}
              />
            </p>
          )}

          {materiau && (
            <p className="font-normal text-sm text-center w-full">
              {t("singleArtworkSubtitleSculptureEditionMateriau") + ": "}
              {materiau}
            </p>
          )}

          {edition.sculptureEditionDescriptionSocle && (
            <p className="font-normal text-sm text-center w-full">
              {t("singleArtworkSubtitleSculptureEditionSocle") + ": "}
              {edition.sculptureEditionDescriptionSocle}
            </p>
          )}

          {signature && (
            <p className="font-normal text-sm text-center w-full">
              {t("singleArtworkSubtitleSculptureEditionSignature") + ": "}
              {signature}
            </p>
          )}

          {marqueTampons && (
            <p className="font-normal text-sm text-center w-full">
              {t("singleArtworkSubtitleSculptureEditionMarqueTampons") + ": "}
              {marqueTampons}
            </p>
          )}

          {edition.collector && typeof edition.collector === "object" && (
            <p className="font-normal text-sm text-center w-full">
              {t("tirageDescriptionCollectorLabelFront") + " "}
              {computeFieldCollectorNameForOverview(
                {
                  collectionneursDescriptionNom:
                    edition.collector.collectionneursDescriptionNom ?? "",
                  collectionneursDescriptionPrenom:
                    edition.collector.collectionneursDescriptionPrenom ?? "",
                  collectionneursDescriptionCollectionPublique:
                    edition.collector
                      .collectionneursDescriptionCollectionPublique ?? false,
                  collectionneursDescriptionVille:
                    edition.collector.collectionneursDescriptionVille ?? "",
                  collectionneursDescriptionVillePublished:
                    edition.collector.collectionneursDescriptionVillePublished,
                  collectionneursDescriptionPays:
                    edition.collector.collectionneursDescriptionPays ?? "",
                  collectionneursDescriptionPaysPublished:
                    edition.collector.collectionneursDescriptionPaysPublished,
                  showCity: true,
                },
                { language: locale, translation: t },
              )}
            </p>
          )}

          {displayData.oeuvresCreditsCreditOeuvre && (
            <p className="font-normal text-sm text-center w-full">
              {t("tirageDescriptionCreditLabelFront") + " "}
              {displayData.oeuvresCreditsCreditOeuvre}
            </p>
          )}

          {edition.sculptureEditionRemarquesPubliquesPublished === true &&
            edition.sculptureEditionRemarquesPubliquesMultilingual?.[
              locale
            ] && (
              <div className="my-4 flex flex-col gap-2 font-medium text-sm text-center w-full">
                {t("singleArtworkSubtitlePublicNote")}
                <ReadMoreHtml
                  className="font-normal text-sm"
                  locale={locale}
                  html={
                    edition.sculptureEditionRemarquesPubliquesMultilingual[
                      locale
                    ]
                  }
                />
              </div>
            )}

          {(sculptureProvenanceCalculated ||
            sculptureExhibitionsCalculated ||
            sculptureBibliographyCalculated ||
            sculptureAuctionsCalculated ||
            relationNotEmpty) && (
            <div className="w-3/4 mt-6 mb-8">
              <Accordion
                type="multiple"
                className="font-normal text-base text-gray-800"
              >
                {sculptureProvenanceCalculated && (
                  <HistoryAccordion
                    historyCalculated={null}
                    tirageHistoryCalculated={sculptureProvenanceCalculated}
                    t={t}
                    title={t("sculpture.accordion.provenance.title")}
                  />
                )}
                {sculptureExhibitionsCalculated && (
                  <ExhibitionsAccordion
                    exhibitionsCalculated={sculptureExhibitionsCalculated}
                    t={t}
                  />
                )}
                {sculptureBibliographyCalculated && (
                  <BibliographyAccordion
                    bibliographyCalculated={sculptureBibliographyCalculated}
                    t={t}
                  />
                )}
                {sculptureAuctionsCalculated && (
                  <AuctionsAccordion
                    auctionCalculated={sculptureAuctionsCalculated}
                    t={t}
                  />
                )}
                {relationNotEmpty && (
                  <RelatedWorksAccordion
                    relatedFieldsWithAttachments={
                      relatedFieldsWithAttachments || {}
                    }
                    relationNotEmpty={relationNotEmpty}
                    locale={locale}
                    t={t}
                  />
                )}
              </Accordion>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
